import { Component, ContentChildren, EventEmitter, QueryList, TemplateRef, ViewChild } from '@angular/core';
import { AutocompleteOptionGroupComponent } from '../autocomplete-option-group/autocomplete-option-group.component';
import { AutocompleteOptionDirective } from '../../directives/autocomplete-option.directive';
import { NgTemplateOutlet } from '@angular/common';
import { AutocompleteStateDirective } from '../../directives/autocomplete-state.directive';

@Component({
  selector: 'app-autocomplete',
  exportAs: 'appAutocomplete',
  imports: [NgTemplateOutlet],
  templateUrl: './autocomplete.component.html',
})
export class AutocompleteComponent {
  static nextId = 0;
  id = `app-autocomplete-${AutocompleteComponent.nextId++}`;
  @ViewChild('panel', { read: TemplateRef }) panelTpl!: TemplateRef<any>;
  @ContentChildren(AutocompleteOptionGroupComponent) groups?: QueryList<AutocompleteOptionGroupComponent>;
  @ContentChildren(AutocompleteStateDirective) states?: QueryList<AutocompleteStateDirective>;

  get hasDefaultStateTpl() {
    return this.states?.some((state) => state.type === 'default');
  }

  get hasNoResultsStateTpl() {
    return this.states?.some((state) => state.type === 'no-results');
  }

  searchTerm: string = '';
  initialized: boolean = false;

  /** Active option for keyboard nav */
  private _active: AutocompleteOptionDirective | null = null;

  /** For trigger: announce activedescendant id changes */
  readonly activeDescendantIdChange = new EventEmitter<string | null>();

  /** When an option gets chosen */
  readonly optionSelected = new EventEmitter<AutocompleteOptionDirective>();

  markAsInitialized() {
    this.initialized ||= true;
  }

  setFilterText(text: string) {
    this.searchTerm = text;
    if (!this.searchTerm) {
      this.setActive(null);
    }

    // If the current active option is no longer visible, clear the active state.
    const active = this._active;
    if (active) {
      const stillVisible = this.flattenedOptions.some((o) => o.id === active.id);
      if (!stillVisible) this.setActive(null);
    }
  }

  /** Keyboard helpers */
  moveActive(delta: -1 | 1) {
    const list = this.flattenedOptions;
    const len = list.length;

    if (len === 0) {
      this.setActive(null);
      return;
    }

    // Where are we now?
    const cur = this._active ? list.indexOf(this._active) : -1;

    // If nothing is active yet:
    //  - with +1 start "before" first so we land on 0
    //  - with -1 start at 0 so we wrap to last
    const start = cur === -1 ? (delta === 1 ? -1 : 0) : cur;

    // Circular next index
    const nextIdx = (start + delta + len) % len;

    this.setActive(list[nextIdx]);
  }

  setActiveFirstOption() {
    const list = this.flattenedOptions;
    const first = list.length ? list[0] : null;
    this.setActive(first);
  }

  setActiveLastOption() {
    const list = this.flattenedOptions;
    this.setActive(list.at(-1) ?? null);
  }

  selectActiveOption() {
    if (this._active) {
      this.selectOption(this._active);
      this._active.el.nativeElement.click();
    }
  }

  selectOption(opt: AutocompleteOptionDirective) {
    this.optionSelected.emit(opt);
  }

  /** Internal */
  setActive(opt: AutocompleteOptionDirective | null) {
    if (this._active) {
      this._active.attrAriaSelected = false;
    }
    this._active = opt;

    if (this._active) {
      this._active.attrAriaSelected = true;
    }

    this.activeDescendantIdChange.emit(opt?.id ?? null);
  }

  get flattenedOptions(): AutocompleteOptionDirective[] {
    const options: AutocompleteOptionDirective[] = [];
    for (const group of this.groups?.toArray() ?? []) {
      options.push(...(group.options ?? []));
    }
    return options;
  }
}
